// client/src/writer/hooks/useChapterAIActions.ts
// import { useCallback, useContext } from 'react';
import { useCallback, useState } from 'react';
import { marked } from 'marked';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// import { AppContext } from '../contexts/AppContext';
import { apiRequest } from '../../lib/queryClient';
import { useToast } from '../../hooks/use-toast.js';
import { useChapterContentLoader } from './useChapterContentLoader';
import { cacheManager } from '../../utils/cacheUtils';
import { safeMergeChapters, bumpClientEpoch, getCurrentClientEpoch } from '../../utils/cacheMerge';

// Helper to handle marked parsing safely
const parseMarkdown = async (markdown: string): Promise<string> => {
    try {
        if (typeof marked.parse === 'function') {
            const result = marked.parse(markdown);
            return result instanceof Promise ? await result : result;
        } else {
            return marked(markdown) as string;
        }
    } catch (error) {
        return markdown; // Return original content if parsing fails
    }
};

import { ProjectState } from '../types/project';
import { Chapter, ChapterVersion, LoadingStates, ChapterVitals } from '../types';
import { initialChapterVitals } from '../constants.js';
import { CoreState } from './useCoreState';
import { ModalControls } from './useModalStates';
import { ChapterState } from './useChapterState';
import { Editor } from '@tiptap/react';

export interface ChapterAIActions {
    handleGenerateChapterPreview: () => Promise<void>;
    handleGenerateFullTextFromPreview: (provider?: string, model?: string, onError?: (error: string) => void) => Promise<void>;
    handleGenerateFullTextForAllPreviewedChapters: () => Promise<void>;
    handleGenerateShortPreviewForAllChapters: (provider?: string, model?: string) => Promise<void>;
    handleReviseText: () => Promise<void>;
}

export const useChapterAIActions = (
    coreState: CoreState,
    chapterState: ChapterState,
    modalControls: ModalControls,
    editor: Editor | null
): ChapterAIActions => {
    const {
        projectState, setProjectState, apiKey, setLoadingState, setError, getCoreProjectContextForAI, activeChapter, clearError
    } = coreState;
    const queryClient = useQueryClient();
    const { activeChapterDetailEdits, areChapterDetailsCurrentlyUnsaved, revisionInput, setRevisionInput, revisionWordCount } = chapterState;
    
    // Initialize cache-first chapter content loader
    const { saveChapterContent } = useChapterContentLoader();

    const { toast } = useToast();
    
    // Store error callback for custom error handling
    const [customErrorCallback, setCustomErrorCallback] = useState<((error: string) => void) | null>(null);

    const aiMutation = useMutation({
        mutationFn: async ({ task, payload }: { task: string, payload: any }) => {
            const response = await apiRequest(`/api/llm/${task}`, "POST", payload);
            return response.json();
        },
        onSuccess: (data, variables) => {
            const { task, payload } = variables;
            let successMessage = "AI action successful!";

            // ✅ PROGRESS TRACKING: Use progress data from LLM response (unified progress handler)
            console.log('🎯 Chapter AI task completed with progress data:', {
                task,
                hasProgress: !!data.progress,
                progressKeys: data.progress ? Object.keys(data.progress) : null,
                progress: data.progress
            });

            if (!projectState) return; // Guard: projectState must exist for AI actions

            // CRITICAL FIX: Determine the correct, persistent chapter ID for API calls.
            // The server expects the database UUID, not the temporary client-side ID like 'ch01_void'.
            // We assume the full chapter object in the state has a 'uuid' property from the database.
            const chapterToUpdate = projectState.chapters.find(c => c.id === activeChapter?.id);
            const persistentChapterId = chapterToUpdate?.uuid || activeChapter?.uuid || activeChapter?.id;

            // 🔍 COMPREHENSIVE DEBUG LOGGING: Identify chapter data structure and ID resolution
            console.log('🔍 CHAPTER ID RESOLUTION DEBUG:', {
                task: task,
                activeChapter: activeChapter,
                activeChapterKeys: activeChapter ? Object.keys(activeChapter) : 'NO ACTIVE CHAPTER',
                chapterToUpdate: chapterToUpdate,
                chapterToUpdateKeys: chapterToUpdate ? Object.keys(chapterToUpdate) : 'NO CHAPTER FOUND',
                projectStateChapters: projectState.chapters.map(c => ({
                    id: c.id,
                    uuid: c.uuid || 'NO UUID',
                    title: c.title,
                    availableKeys: Object.keys(c)
                })),
                persistentChapterId: persistentChapterId,
                isValidUUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[4][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(persistentChapterId || ''),
                willCallAPI: `POST /api/chapters/${persistentChapterId}/preview`
            });

            // Validate the ID before making the API call
            if (!persistentChapterId || persistentChapterId.includes('_') || persistentChapterId.startsWith('ch')) {
                const errorMsg = `Invalid chapter ID: ${persistentChapterId}. Cannot save to database with temporary ID.`;
                console.error('❌', errorMsg);
                toast({ title: "ID Error", description: errorMsg, variant: "destructive" });
                throw new Error(errorMsg);
            }

            if (task === 'chapter-preview') {
                const { chapterContent: markdownContent, chapterSummary } = data.data;
                parseMarkdown(markdownContent).then(async htmlContent => {
                    // 🔧 DECLARE VARIABLE OUTSIDE TRY BLOCK: Fix scope issue
                    let updatedChaptersForState = projectState.chapters;
                    let saveSuccessful = false;

                    // Save to database immediately
                    try {
                        const projectIdUsedForApi = projectState?.projectId || projectState?.currentProjectName;

                        // 🔍 CANONICAL ID GUARD: Ensure consistent project identity
                        console.log('CANON CHECK', {
                            projectIdUsedForApi,          // must be the canonical one
                            projectIdInURL: window.location.pathname.split('/').pop(),
                            chapterProjectId: activeChapter?.projectId,  // should match canonical projectId
                            chapterId: persistentChapterId,
                            activeChapterAllFields: Object.keys(activeChapter || {}),
                            projectStateFields: Object.keys(projectState || {})
                        });

                        await apiRequest(`/api/chapters/${persistentChapterId}/preview`, 'POST', {
                            previewContent: htmlContent,
                            previewSummary: chapterSummary,
                            previewWordCountTarget: payload.targetPreviewWordCount,
                        });

                        toast({ title: "Preview Saved", description: "Chapter preview content has been saved to the database." });

                        // ✅ FIXED: Update chapters cache directly with preview data instead of just invalidating
                        try {
                            const canonicalProjectId = projectIdUsedForApi;
                            if (canonicalProjectId) {

                                // Update chapters cache with new preview data immediately
                                const updatedChapter = {
                                    ...activeChapter,
                                    previewContent: htmlContent,
                                    previewSummary: chapterSummary,
                                    previewWordCountTarget: payload.targetPreviewWordCount,
                                    status: 'preview_generated',
                                    progress: {
                                        ...activeChapter?.progress,
                                        hasPreviewText: true,
                                        progressPercentage: Math.min((activeChapter?.progress?.progressPercentage || 0) + 50, 100)
                                    },
                                    contentCache: {
                                        ...activeChapter?.contentCache,
                                        hasPreview: true,
                                        previewWordCount: payload.targetPreviewWordCount || 0
                                    }
                                };

                                // Get current chapters from cache and update the specific chapter
                                const currentChapters = queryClient.getQueryData(['project-chapters', canonicalProjectId, 'chaptersBuilding']) || [];
                                const updatedChapters = Array.isArray(currentChapters)
                                    ? currentChapters.map(ch => ch.id === activeChapter?.id ? updatedChapter : ch)
                                    : [updatedChapter];

                                // Update all relevant chapters cache keys
                                queryClient.setQueryData(['project-chapters', canonicalProjectId, 'chaptersBuilding'], updatedChapters);
                                queryClient.setQueryData(['project-chapters', canonicalProjectId, 'bookPreview'], updatedChapters);
                                queryClient.setQueryData(['project-chapters', canonicalProjectId], updatedChapters);

                                // Update cacheManager too
                                cacheManager.set('chapters', canonicalProjectId, updatedChapters, 10 * 60 * 1000);

                                // Update localStorage cache
                                const cacheKey = `chapters_${canonicalProjectId}`;
                                localStorage.setItem(cacheKey, JSON.stringify(updatedChapters));

                                console.log('✅ Updated chapters cache with preview data:', {
                                    chapterId: activeChapter?.id,
                                    hasPreview: true,
                                    previewWordCount: payload.targetPreviewWordCount
                                });

                                // ✅ UPDATE PROJECT SUMMARY CACHE with progress data from LLM response
                                if (data.progress) {
                                    const currentSummary = queryClient.getQueryData(['story-project-summary', canonicalProjectId]);
                                    if (currentSummary) {
                                        const updatedSummary = {
                                            ...currentSummary,
                                            progress: data.progress
                                        };
                                        queryClient.setQueryData(['story-project-summary', canonicalProjectId], updatedSummary);

                                        // Also update cacheManager
                                        cacheManager.set('project-summary', canonicalProjectId, updatedSummary, 5 * 60 * 1000);

                                    }
                                }

                                // Still invalidate summaries list for dashboard updates
                                queryClient.invalidateQueries({queryKey: ['/api/story-projects/summaries']});
                            }
                        } catch (progressError) {
                            console.error('❌ Failed to update chapters cache:', progressError);
                        }

                        // 🔧 ONLY UPDATE UI STATUS AFTER SAVE SUCCESS: Update state inside try block
                        updatedChaptersForState = (projectState?.chapters ?? []).map(c => c.id === activeChapter?.id ? {
                            ...c, previewContent: htmlContent, previewSummary: chapterSummary, previewWordCountTarget: payload.targetPreviewWordCount, status: 'preview_generated' as const
                        } : c);

                        setProjectState(prev => {
                            if (!prev) return prev;
                            return { ...prev, chapters: updatedChaptersForState, isUnsaved: true };
                        });

                        saveSuccessful = true;

                    } catch (error: any) {
                        console.error('Failed to save preview to database:', error);
                        toast({ title: "Save Error", description: `Failed to save preview: ${error.message}`, variant: "destructive" });
                        // 🔧 DO NOT UPDATE LOCAL STATE ON SAVE FAILURE
                        saveSuccessful = false;
                    }
                    
                    const projectId = projectState?.projectId || projectState?.currentProjectName;
                    if (!projectId) {
                        console.error('❌ No project ID found for progress update');
                        return;
                    }

                    // 🔧 ONLY DO OPTIMISTIC UPDATE IF SAVE WAS SUCCESSFUL
                    if (!saveSuccessful) {
                        return;
                    }

                    // --- OPTIMISTIC UI UPDATE FOR PROGRESS ---
                    
                    // 1. Calculate the new progress locally.
                    // Logic: 50% for a preview, 100% for full text.
                    const calculateLocalProgress = (chapters: Chapter[]) => {
                        if (!chapters || chapters.length === 0) return 0;
                        let progressPoints = 0;
                        chapters.forEach(chap => {
                            if (chap.status === 'text_generated' || (chap.history?.length ?? 0) > 0) {
                                progressPoints += 1;
                            } else if (chap.status === 'preview_generated' || chap.previewContent) {
                                progressPoints += 0.5;
                            }
                        });
                        return Math.round((progressPoints / chapters.length) * 100);
                    };
                    
                    const newOptimisticChaptersProgress = calculateLocalProgress(updatedChaptersForState);
                    
                    // 2. Update all client caches immediately with the optimistic value.
                    const oldSummary: any = queryClient.getQueryData(['story-project-summary', projectId]) || {};
                    const newOptimisticProgress = {
                        ...(oldSummary.progress || {}),
                        chapters: newOptimisticChaptersProgress,
                        // Also update overall progress if possible (simplified for now)
                        overall: Math.max(oldSummary.progress?.overall || 0, newOptimisticChaptersProgress) 
                    };

                    const optimisticSummary = {
                        ...oldSummary,
                        progress: newOptimisticProgress,
                        updatedAt: new Date().toISOString()
                    };
                    
                    queryClient.setQueryData(['story-project-summary', projectId], optimisticSummary);
                    cacheManager.set('project-summary', projectId, optimisticSummary, 5 * 60 * 1000);
                    
                    // 3. In the background, fetch the authoritative progress from the server to ensure eventual consistency.
                    // We add a longer delay to give the server ample time to finish its calculations.
                    const requestEpoch = getCurrentClientEpoch(); // Capture current epoch for this request
                    setTimeout(async () => {
                        try {
                            const progressResponse = await apiRequest(`/api/story-projects/${projectId}/summary`, "GET");
                            const serverData = await progressResponse.json();

                            if (serverData && serverData.progress) {

                                // 🛡️ CHAPTER-AWARE RECONCILIATION: Only update numeric progress, protect chapter flags
                                queryClient.setQueryData(['story-project-summary', projectId], (prev: any) => {
                                    if (!prev) return prev;

                                    // Check if this server response is stale compared to client writes
                                    const prevEpoch = prev.__clientWriteEpoch ?? 0;
                                    if (prevEpoch > requestEpoch) {
                                        console.log('⏰ Ignoring stale server response, client has newer data:', {
                                            serverEpoch: requestEpoch,
                                            clientEpoch: prevEpoch
                                        });
                                        return prev; // Keep current data
                                    }

                                    // Get current chapters from cache to ensure we never downgrade
                                    const chaptersCache = queryClient.getQueryData<Chapter[]>(['project-chapters', projectId]) ?? [];

                                    // Detailed comparison for critical fields
                                    const clientChaptersProgress = prev.progress?.chapters ?? 0;
                                    const serverChaptersProgress = serverData.progress?.chapters ?? 0;
                                    const clientOutlineProgress = prev.progress?.chaptersOutline ?? 0;
                                    const serverOutlineProgress = serverData.progress?.chaptersOutline ?? 0;

                                    if (serverChaptersProgress < clientChaptersProgress || serverOutlineProgress < clientOutlineProgress) {
                                        console.warn('⚠️ Server has stale chapter progress - protecting client data:', {
                                            chapters: { client: clientChaptersProgress, server: serverChaptersProgress },
                                            outline: { client: clientOutlineProgress, server: serverOutlineProgress }
                                        });
                                    }

                                    const reconciledSummary = {
                                        ...prev,
                                        // Smart progress merging: never downgrade client progress with stale server data
                                        progress: {
                                            ...prev.progress,
                                            // Only accept server progress if it's higher than client progress
                                            overall: Math.max(prev.progress?.overall ?? 0, serverData.progress?.overall ?? 0),
                                            metadata: Math.max(prev.progress?.metadata ?? 0, serverData.progress?.metadata ?? 0),
                                            blueprint: Math.max(prev.progress?.blueprint ?? 0, serverData.progress?.blueprint ?? 0),
                                            // NEVER downgrade chapters-related progress (protect from stale server data)
                                            chapters: Math.max(prev.progress?.chapters ?? 0, serverData.progress?.chapters ?? 0),
                                            chaptersOutline: Math.max(prev.progress?.chaptersOutline ?? 0, serverData.progress?.chaptersOutline ?? 0),
                                            // Accept other fields normally (server is authoritative for these)
                                            export: serverData.progress?.export ?? prev.progress?.export ?? 0,
                                            images: serverData.progress?.images ?? prev.progress?.images ?? 0,
                                            frontMatter: serverData.progress?.frontMatter ?? prev.progress?.frontMatter ?? 0,
                                            backMatter: serverData.progress?.backMatter ?? prev.progress?.backMatter ?? 0,
                                            authorDetails: serverData.progress?.authorDetails ?? prev.progress?.authorDetails ?? 0
                                        }
                                    };

                                    // 🛡️ NEVER let server downgrade chapter flags when cache has data
                                    if (chaptersCache.length > 0) {
                                        reconciledSummary.hasChapters = true;
                                        reconciledSummary.chapterCount = Math.max(
                                            serverData.chapterCount ?? 0,
                                            prev.chapterCount ?? 0,
                                            chaptersCache.length
                                        );
                                        console.log('🛡️ Protected chapter flags during reconciliation:', {
                                            hasChapters: true,
                                            chapterCount: reconciledSummary.chapterCount,
                                            serverCount: serverData.chapterCount,
                                            cacheCount: chaptersCache.length
                                        });
                                    } else {
                                        // Only accept server flags if we have no chapters locally
                                        reconciledSummary.hasChapters = !!serverData.hasChapters;
                                        reconciledSummary.chapterCount = serverData.chapterCount ?? prev.chapterCount ?? 0;
                                    }

                                    return reconciledSummary;
                                });

                                // Update cacheManager with reconciled data
                                const reconciledData = queryClient.getQueryData(['story-project-summary', projectId]);
                                if (reconciledData) {
                                    cacheManager.set('project-summary', projectId, reconciledData, 5 * 60 * 1000);
                                }
                            } else {
                                console.warn('⚠️ No progress data received from server reconciliation call.');
                            }
                        } catch (error) {
                            console.error('❌ Failed to fetch authoritative progress:', error);
                        }
                    }, 2000); // Wait 2 seconds before reconciling.
                });
                successMessage = "Chapter preview generated and saved.";
            } else if (task === 'chapter-full-text') {
                const { chapterContent: markdownContent } = data.data;
                parseMarkdown(markdownContent).then(async htmlContent => {
                    // Save to database and cache immediately (no generatedSummary since we're not generating it anymore)
                    let saveSuccessful = false;
                    let updatedChaptersForState: Chapter[] = [];

                    try {
                        const projectId = projectState?.projectId || projectState?.currentProjectName || 'unknown';
                        await saveChapterContent(
                            projectId,
                            persistentChapterId,
                            htmlContent,
                            undefined // No summary for full text generation
                        );

                        // Calculate word count for contentCache
                        const wordCount = htmlContent.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length;

                        // Update chapters with proper contentCache structure
                        const canonicalProjectId = projectState?.projectId || projectState?.currentProjectName || 'unknown';
                        updatedChaptersForState = (projectState?.chapters ?? []).map(c => c.id === activeChapter?.id ? {
                            ...c,
                            history: [{ content: htmlContent, generatedSummary: null, timestamp: Date.now(), previewText: markdownContent.substring(0, 200) + '...' } as ChapterVersion],
                            historyIndex: 0,
                            status: 'text_generated' as const,
                            previewContent: c.previewContent, // 🔧 PRESERVE existing preview content instead of clearing it
                            previewSummary: c.previewSummary, // 🔧 PRESERVE existing preview summary instead of clearing it
                            previewWordCountTarget: c.previewWordCountTarget || 0, // 🔧 PRESERVE existing word count target
                            contentCache: {
                                hasPreview: !!c.previewContent,
                                hasFullText: true,
                                fullTextWordCount: wordCount
                            }
                        } : c);

                        // Update all relevant chapters cache keys (same pattern as preview)
                        queryClient.setQueryData(['project-chapters', canonicalProjectId, 'chaptersBuilding'], updatedChaptersForState);
                        queryClient.setQueryData(['project-chapters', canonicalProjectId, 'bookPreview'], updatedChaptersForState);
                        queryClient.setQueryData(['project-chapters', canonicalProjectId], updatedChaptersForState);

                        // Update cacheManager too
                        cacheManager.set('chapters', canonicalProjectId, updatedChaptersForState, 10 * 60 * 1000);
                        const cacheKey = `chapters_${canonicalProjectId}`;
                        localStorage.setItem(cacheKey, JSON.stringify(updatedChaptersForState));

                        // Update local state
                        setProjectState(prev => {
                            if (!prev) return prev;
                            return { ...prev, chapters: updatedChaptersForState, isUnsaved: true };
                        });

                        saveSuccessful = true;

                    } catch (error: any) {
                        console.error('Failed to save full text to database:', error);
                        toast({ title: "Save Error", description: `Failed to save full text: ${error.message}`, variant: "destructive" });
                        saveSuccessful = false;
                    }

                    const projectId = projectState?.projectId || projectState?.currentProjectName;
                    if (!projectId) {
                        console.error('❌ No project ID found for progress update');
                        return;
                    }

                    // ONLY DO OPTIMISTIC UPDATE IF SAVE WAS SUCCESSFUL
                    if (!saveSuccessful) {
                        return;
                    }

                    // --- OPTIMISTIC UI UPDATE FOR PROGRESS (same pattern as preview) ---

                    // Calculate the new progress locally
                    const calculateLocalProgress = (chapters: Chapter[]) => {
                        if (!chapters || chapters.length === 0) return 0;
                        let progressPoints = 0;
                        chapters.forEach(chap => {
                            if (chap.status === 'text_generated' || (chap.history?.length ?? 0) > 0) {
                                progressPoints += 1;
                            } else if (chap.status === 'preview_generated' || chap.previewContent) {
                                progressPoints += 0.5;
                            }
                        });
                        return Math.round((progressPoints / chapters.length) * 100);
                    };

                    const newOptimisticChaptersProgress = calculateLocalProgress(updatedChaptersForState);

                    // Update all client caches immediately with the optimistic value
                    const oldSummary: any = queryClient.getQueryData(['story-project-summary', projectId]) || {};
                    const newOptimisticProgress = {
                        ...(oldSummary.progress || {}),
                        chapters: newOptimisticChaptersProgress,
                        // Also update overall progress
                        overall: Math.max(oldSummary.progress?.overall || 0, newOptimisticChaptersProgress)
                    };

                    const optimisticSummary = {
                        ...oldSummary,
                        progress: newOptimisticProgress,
                        updatedAt: new Date().toISOString(),
                        hasChapters: true,
                        chapterCount: Math.max(oldSummary.chapterCount || 0, updatedChaptersForState.length)
                    };

                    queryClient.setQueryData(['story-project-summary', projectId], optimisticSummary);
                    cacheManager.set('project-summary', projectId, optimisticSummary, 5 * 60 * 1000);

                    // In the background, fetch the authoritative progress from the server (same pattern as preview)
                    const requestEpoch = getCurrentClientEpoch();
                    setTimeout(async () => {
                        try {
                            const progressResponse = await apiRequest(`/api/story-projects/${projectId}/summary`, "GET");
                            const serverData = await progressResponse.json();

                            if (serverData && serverData.progress) {

                                // CHAPTER-AWARE RECONCILIATION: Only update numeric progress, protect chapter flags
                                queryClient.setQueryData(['story-project-summary', projectId], (prev: any) => {
                                    if (!prev) return prev;

                                    // Check if this server response is stale compared to client writes
                                    const prevEpoch = prev.__clientWriteEpoch ?? 0;
                                    if (prevEpoch > requestEpoch) {
                                        console.log('⏰ Ignoring stale server response, client has newer data:', {
                                            serverEpoch: requestEpoch,
                                            clientEpoch: prevEpoch
                                        });
                                        return prev;
                                    }

                                    // Get current chapters from cache to ensure we never downgrade
                                    const chaptersCache = queryClient.getQueryData<Chapter[]>(['project-chapters', projectId]) ?? [];

                                    const reconciledSummary = {
                                        ...prev,
                                        // Smart progress merging: never downgrade client progress with stale server data
                                        progress: {
                                            ...prev.progress,
                                            // Only accept server progress if it's higher than client progress
                                            overall: Math.max(prev.progress?.overall ?? 0, serverData.progress?.overall ?? 0),
                                            metadata: Math.max(prev.progress?.metadata ?? 0, serverData.progress?.metadata ?? 0),
                                            blueprint: Math.max(prev.progress?.blueprint ?? 0, serverData.progress?.blueprint ?? 0),
                                            // NEVER downgrade chapters-related progress (protect from stale server data)
                                            chapters: Math.max(prev.progress?.chapters ?? 0, serverData.progress?.chapters ?? 0),
                                            chaptersOutline: Math.max(prev.progress?.chaptersOutline ?? 0, serverData.progress?.chaptersOutline ?? 0),
                                            // Accept other fields normally
                                            export: serverData.progress?.export ?? prev.progress?.export ?? 0,
                                            images: serverData.progress?.images ?? prev.progress?.images ?? 0,
                                            frontMatter: serverData.progress?.frontMatter ?? prev.progress?.frontMatter ?? 0,
                                            backMatter: serverData.progress?.backMatter ?? prev.progress?.backMatter ?? 0,
                                            authorDetails: serverData.progress?.authorDetails ?? prev.progress?.authorDetails ?? 0
                                        }
                                    };

                                    // NEVER let server downgrade chapter flags when cache has data
                                    if (chaptersCache.length > 0) {
                                        reconciledSummary.hasChapters = true;
                                        reconciledSummary.chapterCount = Math.max(
                                            serverData.chapterCount ?? 0,
                                            prev.chapterCount ?? 0,
                                            chaptersCache.length
                                        );
                                        console.log('🛡️ Protected chapter flags during full text reconciliation:', {
                                            hasChapters: true,
                                            chapterCount: reconciledSummary.chapterCount,
                                            serverCount: serverData.chapterCount,
                                            cacheCount: chaptersCache.length
                                        });
                                    } else {
                                        reconciledSummary.hasChapters = !!serverData.hasChapters;
                                        reconciledSummary.chapterCount = serverData.chapterCount ?? prev.chapterCount ?? 0;
                                    }

                                    return reconciledSummary;
                                });

                                // Update cacheManager with reconciled data
                                const reconciledData = queryClient.getQueryData(['story-project-summary', projectId]);
                                if (reconciledData) {
                                    cacheManager.set('project-summary', projectId, reconciledData, 5 * 60 * 1000);
                                }
                            } else {
                                console.warn('⚠️ No progress data received from server reconciliation call for full text.');
                            }
                        } catch (error) {
                            console.error('❌ Failed to fetch authoritative progress for full text:', error);
                        }
                    }, 2000); // Wait 2 seconds before reconciling

                    // FIX: The second argument for setContent should be an options object.
                    if (editor) editor.commands.setContent(htmlContent, false);
                });
                successMessage = "Full chapter text generated and saved.";
            } else if (task === 'chapter-revise') {
                const { revisedContent: markdownRevisedContent, revisedSummary } = data.data;
                parseMarkdown(markdownRevisedContent).then(async htmlRevisedContent => {
                    // Save revision to database and cache immediately
                    try {
                        const projectId = projectState?.projectId || projectState?.currentProjectName || 'unknown';
                        await saveChapterContent(
                            projectId,
                            persistentChapterId,
                            htmlRevisedContent,
                            revisedSummary,
                            payload.revisionInput
                        );
                    } catch (error: any) {
                        console.error('Failed to save revision to database:', error);
                        toast({ title: "Save Error", description: `Failed to save revision: ${error.message}`, variant: "destructive" });
                    }

                    const newVersion: ChapterVersion = { content: htmlRevisedContent, generatedSummary: revisedSummary, timestamp: Date.now(), revisionNotes: payload.revisionInput, previewText: markdownRevisedContent.substring(0, 200) + '...' }; 
                    setProjectState(prev => {
                        if (!prev) return prev;
                        const chap = prev.chapters?.find(c => c.id === activeChapter?.id);
                        if (!chap) return {...prev, isUnsaved: true};
                        const history = chap.history ?? [];
                        const historyIndex = chap.historyIndex ?? history.length - 1;
                        const newHistory = [...history.slice(0, historyIndex + 1), newVersion];
                        return {
                            ...prev,
                            chapters: prev.chapters.map(c => c.id === activeChapter?.id ? { ...c, history: newHistory, historyIndex: newHistory.length - 1 } : c),
                            isUnsaved: true,
                        };
                    });
                    setRevisionInput(''); 
                    // FIX: The second argument for setContent should be an options object.
                    if (editor) editor.commands.setContent(htmlRevisedContent, false);
                });
                successMessage = "Chapter revised and saved.";
            }
            toast({ title: "Success", description: successMessage });
        },
        onError: (error: any) => {
            setError(`AI Error: ${error.message}`);
            toast({ title: "AI Error", description: error.message, variant: "destructive" });
            if (error.rawResponse) modalControls.openDebugResponseModal(error.rawResponse);
            
            // Call custom error callback if provided
            if (customErrorCallback) {
                customErrorCallback(error.message);
            }
        },
        onSettled: (data, error, variables) => {
            const { task } = variables;
            if (task === 'chapter-preview') setLoadingState('genPreview', false);
            else if (task === 'chapter-full-text') setLoadingState('genText', false);
            else if (task === 'chapter-revise') setLoadingState('reviseText', false);
            else if (task === 'chapter-full-text-batch') setLoadingState('genAllPreviewsToFull', false);
            else if (task === 'chapter-preview-batch') setLoadingState('genAllShortPreviews', false);
        }
    });

    const handleGenerateChapterPreview = useCallback(async () => {
        console.log('🚀 handleGenerateChapterPreview called!', {
            activeChapter: activeChapter ? 'PRESENT' : 'MISSING',
            activeChapterDetailEdits: activeChapterDetailEdits ? 'PRESENT' : 'MISSING'
        });
        
        // Note: API keys are handled server-side with environment variables
        // No need to check for user-specific API keys
        if (!activeChapter || !activeChapterDetailEdits) { 
            console.error('❌ Missing chapter data!', { activeChapter, activeChapterDetailEdits });
            setError("No active chapter or details form to use for generation."); 
            return; 
        }
        // Removed the areChapterDetailsCurrentlyUnsaved check - allow generation with current data
        
        const fullWordCount = parseInt(activeChapterDetailEdits.expectedWordCount || (activeChapter?.expectedWordCount ?? 0).toString(), 10);
        if (isNaN(fullWordCount) || fullWordCount <= 0) { setError("Invalid expected word count for the full chapter."); return; }
        
        if (!projectState) { setError("No project loaded."); return; }
        const targetPreviewWordCount = Math.round(fullWordCount * (projectState.chapterPreviewPercentage / 100));
        if (targetPreviewWordCount <=0) { setError("Calculated preview word count is too low. Adjust percentage or full chapter word count."); return;}

        setLoadingState('genPreview', true); setError(null);
        const chapters = projectState.chapters ?? [];
        const chapterIndex = chapters.findIndex(c => c.id === activeChapter.id);
        const prevChapterData = chapterIndex > 0 ? chapters[chapterIndex - 1] : null;
        const nextChapterData = chapterIndex >= 0 && chapterIndex < chapters.length - 1 ? chapters[chapterIndex + 1] : null;

        const chapterContext = {
            title: activeChapterDetailEdits.title || activeChapter.title, summary: activeChapterDetailEdits.summary || activeChapter.summary,
            geography: activeChapterDetailEdits.geography || activeChapter.geography, charactersInvolved: activeChapterDetailEdits.charactersInvolved || activeChapter.charactersInvolved,
            characterActions: activeChapterDetailEdits.characterActions || activeChapter.characterActions, soulOfChapter: activeChapterDetailEdits.soulOfChapter || activeChapter.soulOfChapter,
        };
        const chapterVitalsData = activeChapterDetailEdits.vitals || activeChapter.vitals || initialChapterVitals;

        aiMutation.mutate({
            task: 'chapter-preview',
            payload: {
                projectId: projectState.projectId,
                outlineIdea: projectState.outlineIdea,
                chapterContext,
                targetPreviewWordCount,
                worldBuildingData: projectState.blueprint.worldBuildingSuggestions,
                chapterVitalsData,
                prevChapterData: prevChapterData ? { title: prevChapterData.title, summary: prevChapterData.summary, vitals: prevChapterData.vitals } : null,
                nextChapterData: nextChapterData ? { title: nextChapterData.title, summary: nextChapterData.summary, vitals: nextChapterData.vitals } : null
            }
        });
    }, [apiKey, activeChapter, activeChapterDetailEdits, projectState, setLoadingState, setError, modalControls, aiMutation]);

    const handleGenerateFullTextFromPreview = useCallback(async (provider?: string, model?: string, onError?: (error: string) => void) => {
        // Note: API keys are handled server-side with environment variables
        if (!activeChapter) { setError("No active chapter to generate full text for."); return; }
        if (!projectState) { setError("No project loaded."); return; }

        const wordCount = activeChapter.expectedWordCount ?? 0;
        if (isNaN(wordCount) || wordCount <= 0) { setError("Invalid expected word count for full chapter."); return; }

        // Set the custom error callback for this request
        setCustomErrorCallback(onError ? () => onError : null);

        setLoadingState('genText', true); setError(null);
        const chapters = projectState.chapters ?? [];
        const chapterIndex = chapters.findIndex(c => c.id === activeChapter.id);
        const prevChapterData = chapterIndex > 0 ? chapters[chapterIndex - 1] : null;
        const nextChapterData = chapterIndex >= 0 && chapterIndex < chapters.length - 1 ? chapters[chapterIndex + 1] : null;

        const chapterContext = {
            title: activeChapter.title, summary: activeChapter.summary,
            geography: activeChapter.geography, charactersInvolved: activeChapter.charactersInvolved,
            characterActions: activeChapter.characterActions, soulOfChapter: activeChapter.soulOfChapter,
        };
        const chapterVitalsData = activeChapter.vitals || initialChapterVitals;

        aiMutation.mutate({
            task: 'chapter-full-text',
            payload: {
                outlineIdea: projectState.outlineIdea,
                chapterContext,
                wordCount,
                worldBuildingData: projectState.blueprint.worldBuildingSuggestions,
                chapterVitalsData,
                prevChapterData: prevChapterData ? { title: prevChapterData.title, summary: prevChapterData.summary, vitals: prevChapterData.vitals } : null,
                nextChapterData: nextChapterData ? { title: nextChapterData.title, summary: nextChapterData.summary, vitals: nextChapterData.vitals } : null,
                // Model selection parameters
                preferredProvider: provider,
                preferredModel: model
            }
        });
    }, [apiKey, activeChapter, projectState, setLoadingState, setError, modalControls, aiMutation]);
    
    const handleGenerateFullTextForAllPreviewedChapters = useCallback(async () => {
        // Note: API keys are handled server-side with environment variables
        if (!projectState) { setError("No project loaded."); return; }

        const chapters = projectState.chapters ?? [];
        const chaptersToProcess = chapters.filter(
            ch => ch.previewContent && ch.status !== 'text_generated'
        );

        if (chaptersToProcess.length === 0) {
            setError("No chapters found with previews ready for full text generation.");
            return;
        }

        setLoadingState('genAllPreviewsToFull', true);
        clearError();
        let successCount = 0;
        let lastError: any = null;

        try {
            for (let i = 0; i < chaptersToProcess.length; i++) {
                const chapterToProcess = chaptersToProcess[i];
                const wordCount = chapterToProcess.expectedWordCount ?? 0;
                if (isNaN(wordCount) || wordCount <= 0) { console.warn(`Skipping chapter "${chapterToProcess.title}" due to invalid word count.`); continue; }

                const chapterIndexInFullList = chapters.findIndex(c => c.id === chapterToProcess.id);
                const prevChapterData = chapterIndexInFullList > 0 ? chapters[chapterIndexInFullList - 1] : null;
                const nextChapterData = chapterIndexInFullList >= 0 && chapterIndexInFullList < chapters.length - 1 ? chapters[chapterIndexInFullList + 1] : null;

                const chapterContext = { 
                    title: chapterToProcess.title, summary: chapterToProcess.summary, geography: chapterToProcess.geography, 
                    charactersInvolved: chapterToProcess.charactersInvolved, characterActions: chapterToProcess.characterActions, 
                    soulOfChapter: chapterToProcess.soulOfChapter,
                };
                const chapterVitalsData = chapterToProcess.vitals || initialChapterVitals;

                const payload = {
                    outlineIdea: projectState.outlineIdea,
                    chapterContext,
                    wordCount,
                    worldBuildingData: projectState.blueprint.worldBuildingSuggestions,
                    chapterVitalsData,
                    prevChapterData: prevChapterData ? { title: prevChapterData.title, summary: prevChapterData.summary, vitals: prevChapterData.vitals } : null,
                    nextChapterData: nextChapterData ? { title: nextChapterData.title, summary: nextChapterData.summary, vitals: nextChapterData.vitals } : null
                };

                const response = await apiRequest(`/api/llm/chapter-full-text`, "POST", payload);
                const data = await response.json();
                const { chapterContent: markdownContent } = data.data;
                const htmlContent = await parseMarkdown(markdownContent);

                // CRITICAL FIX: Save to database immediately (like individual full text generation)
                try {
                    const projectId = projectState?.projectId || projectState?.currentProjectName || 'unknown';
                    const persistentChapterId = chapterToProcess.uuid || chapterToProcess.id; // Use UUID if available
                    await saveChapterContent(
                        projectId,
                        persistentChapterId,
                        htmlContent,
                        undefined // No summary for full text generation
                    );
                } catch (dbError: any) {
                    console.error(`Failed to save full text to database for chapter ${chapterToProcess.title}:`, dbError);
                    // Continue with local state update even if database save fails
                }

                const newVersion: ChapterVersion = { content: htmlContent, generatedSummary: null, timestamp: Date.now(), previewText: markdownContent.substring(0, 200) + '...' };

                setProjectState(prev => {
                    if (!prev) return prev;
                    const updatedChapters: Chapter[] = prev.chapters.map(c =>
                        c.id === chapterToProcess.id
                            ? { ...c, history: [newVersion], historyIndex: 0, status: 'text_generated' as const, previewContent: c.previewContent, previewSummary: c.previewSummary, previewWordCountTarget: c.previewWordCountTarget || 0 }
                            : c
                    );
                    return { ...prev, chapters: updatedChapters, isUnsaved: true };
                });
                successCount++;
                if (activeChapter?.id === chapterToProcess.id && editor) {
                    // FIX: The second argument for setContent should be an options object.
                    editor.commands.setContent(htmlContent, false);
                }
            }

            // 🚀 PERFORMANCE FIX: Use batched cache invalidation to prevent excessive React Query rerenders
            if (successCount > 0) {
                const canonicalProjectId = projectState?.projectId || projectState?.currentProjectName;

                if (canonicalProjectId) {

                    // Use batched invalidation instead of multiple individual calls
                    cacheManager.scheduleBatchedInvalidation(
                        queryClient,
                        canonicalProjectId,
                        [
                            { queryKey: ['project-chapters', canonicalProjectId] },
                            { queryKey: ["story-project", canonicalProjectId] },
                            { queryKey: ['story-project-summary', canonicalProjectId] },
                            { queryKey: ['project-progress', canonicalProjectId] },
                            { queryKey: ['/api/story-projects/summaries'] }
                        ],
                        150 // Slightly longer delay for bulk operations
                    );

                    toast({ title: "Success", description: `${successCount} chapter(s) had full text generated and saved.` });
                }
            } else if (chaptersToProcess.length > 0) {
                toast({ title: "Info", description: `No chapters processed. Some may have had invalid word counts.` });
            }
        } catch (e: any) { 
            lastError = e;
            setError(`Batch generation failed for "${chaptersToProcess[successCount]?.title || 'a chapter'}": ${e.message}`);
            toast({ title: "AI Error", description: e.message, variant: "destructive" });
            if (e.rawResponse) modalControls.openDebugResponseModal(e.rawResponse);
        } finally {
            setLoadingState('genAllPreviewsToFull', false);
            if (!lastError && successCount === 0 && chaptersToProcess.length > 0) setError("No chapters eligible or successfully processed.");
        }
    }, [apiKey, projectState, activeChapter, editor, setLoadingState, setError, modalControls, clearError, setProjectState, toast]);

    const handleGenerateShortPreviewForAllChapters = useCallback(async (provider?: string, model?: string) => {
        // Note: API keys are handled server-side with environment variables
        if (!projectState) { setError("No project loaded."); return; }

        const chapters = projectState.chapters ?? [];
        const chaptersToProcess = chapters.filter(ch => !ch.previewContent && ch.status !== 'text_generated');
        if (chaptersToProcess.length === 0) { setError("No chapters eligible for preview generation."); return; }

        setLoadingState('genAllShortPreviews', true);
        clearError();
        let successCount = 0;
        let lastError: any = null;
        let latestProgressData: any = null; // Track progress from latest successful response
        let updatedChaptersForCache: Chapter[] = [...projectState.chapters]; // Track chapters for cache updates

        // 🚀 Bump client write epoch to protect against stale server responses
        const currentEpoch = bumpClientEpoch();

        try {
            for (let i = 0; i < chaptersToProcess.length; i++) {
                const chapterToProcess = chaptersToProcess[i];
                const fullWordCount = chapterToProcess.expectedWordCount ?? 0;
                if (isNaN(fullWordCount) || fullWordCount <= 0) { console.warn(`Skipping preview for "${chapterToProcess.title}" due to invalid word count.`); continue; }
                
                const targetPreviewWordCount = Math.round(fullWordCount * (projectState.chapterPreviewPercentage / 100));
                if (targetPreviewWordCount <=0) { console.warn(`Skipping preview for "${chapterToProcess.title}" due to low calculated preview words.`); continue; }

                const chapterIndexInFullList = chapters.findIndex(c => c.id === chapterToProcess.id);
                const prevChapterData = chapterIndexInFullList > 0 ? chapters[chapterIndexInFullList - 1] : null;
                const nextChapterData = chapterIndexInFullList >= 0 && chapterIndexInFullList < chapters.length - 1 ? chapters[chapterIndexInFullList + 1] : null;

                const chapterContext = { 
                    title: chapterToProcess.title, summary: chapterToProcess.summary, geography: chapterToProcess.geography, 
                    charactersInvolved: chapterToProcess.charactersInvolved, characterActions: chapterToProcess.characterActions, 
                    soulOfChapter: chapterToProcess.soulOfChapter,
                };
                const chapterVitalsData = chapterToProcess.vitals || initialChapterVitals;

                const payload = {
                    projectId: projectState.projectId, // 🚀 SURGICAL FIX: Always pass projectId to preview tasks (batch)
                    outlineIdea: projectState.outlineIdea,
                    chapterContext,
                    targetPreviewWordCount,
                    worldBuildingData: projectState.blueprint.worldBuildingSuggestions,
                    chapterVitalsData,
                    prevChapterData: prevChapterData ? { title: prevChapterData.title, summary: prevChapterData.summary, vitals: prevChapterData.vitals } : null,
                    nextChapterData: nextChapterData ? { title: nextChapterData.title, summary: nextChapterData.summary, vitals: nextChapterData.vitals } : null,
                    // Add selected model parameters if provided (server expects preferredProvider/preferredModel)
                    preferredProvider: provider,
                    preferredModel: model
                };

                const response = await apiRequest(`/api/llm/chapter-preview`, "POST", payload);
                const data = await response.json();
                const { chapterContent: markdownContent, chapterSummary } = data.data;
                const htmlContent = await parseMarkdown(markdownContent);

                // ✅ PROGRESS TRACKING: Capture progress from each successful response
                if (data.progress) {
                    latestProgressData = data.progress;
                }

                // CRITICAL FIX: Save to database immediately (like individual preview generation)
                try {
                    const persistentChapterId = chapterToProcess.uuid || chapterToProcess.id; // Use UUID if available
                    await apiRequest(`/api/chapters/${persistentChapterId}/preview`, 'POST', {
                        previewContent: htmlContent,
                        previewSummary: chapterSummary,
                        previewWordCountTarget: targetPreviewWordCount,
                    });
                } catch (dbError: any) {
                    console.error(`Failed to save preview to database for chapter ${chapterToProcess.title}:`, dbError);
                    // Continue with local state update even if database save fails
                }

                // Update local state
                setProjectState(prev => {
                    if (!prev) return prev;
                    const updatedChapters = prev.chapters.map(c =>
                        c.id === chapterToProcess.id
                            ? { ...c, previewContent: htmlContent, previewSummary: chapterSummary, previewWordCountTarget: targetPreviewWordCount }
                            : c
                    );
                    return { ...prev, chapters: updatedChapters, isUnsaved: true };
                });

                // ✅ CRITICAL FIX: Update the cache tracking array with complete chapter data including contentCache
                updatedChaptersForCache = updatedChaptersForCache.map(c =>
                    c.id === chapterToProcess.id
                        ? {
                            ...c,
                            previewContent: htmlContent,
                            previewSummary: chapterSummary,
                            previewWordCountTarget: targetPreviewWordCount,
                            status: 'preview_generated' as const,
                            progress: {
                                ...c.progress,
                                hasPreviewText: true,
                                progressPercentage: Math.min((c.progress?.progressPercentage || 0) + 50, 100)
                            },
                            contentCache: {
                                ...c.contentCache,
                                hasPreview: true,
                                previewWordCount: targetPreviewWordCount || 0
                            }
                        }
                        : c
                );

                successCount++;
            }

            // ✅ FIXED: Update React Query cache directly with batch preview data
            if (successCount > 0 && projectState?.projectId) {
                // ✅ CRITICAL FIX: Use the cache tracking array instead of stale projectState
                const updatedChapters = updatedChaptersForCache;

                // ✅ SAFE MERGE: Update all relevant chapters cache keys using safeMergeChapters
                queryClient.setQueryData(['project-chapters', projectState.projectId, 'chaptersBuilding'], (prev: any) =>
                    safeMergeChapters(prev, updatedChapters as any)
                );
                queryClient.setQueryData(['project-chapters', projectState.projectId, 'bookPreview'], (prev: any) =>
                    safeMergeChapters(prev, updatedChapters as any)
                );
                queryClient.setQueryData(['project-chapters', projectState.projectId], (prev: any) =>
                    safeMergeChapters(prev, updatedChapters as any)
                );

                // Update cacheManager chapters cache with batch preview data
                cacheManager.set('chapters', projectState.projectId, updatedChapters, 10 * 60 * 1000);
                const cacheKey = `chapters_${projectState.projectId}`;
                localStorage.setItem(cacheKey, JSON.stringify(updatedChapters));

                // ✅ CHAPTER-AWARE SUMMARY UPDATE: Merge progress data while respecting chapters cache
                if (latestProgressData) {
                    queryClient.setQueryData(['story-project-summary', projectState.projectId], (prev: any) => {
                        if (!prev) return prev;

                        // Get current chapters from cache to ensure summary reflects reality
                        const chaptersCache = queryClient.getQueryData<Chapter[]>(['project-chapters', projectState.projectId]) ?? [];

                        const updatedSummary = {
                            ...prev,
                            // Merge numeric progress from LLM response
                            progress: {
                                ...prev.progress,
                                ...latestProgressData
                            },
                            // Add client write epoch for stale response protection
                            __clientWriteEpoch: currentEpoch
                        };

                        // 🛡️ CRITICAL: Ensure summary reflects actual chapter cache state
                        if (chaptersCache.length > 0) {
                            updatedSummary.hasChapters = true;
                            updatedSummary.chapterCount = Math.max(prev.chapterCount ?? 0, chaptersCache.length);
                            console.log('🛡️ Protected summary from stale server flags using cache:', {
                                hasChapters: true,
                                chapterCount: updatedSummary.chapterCount,
                                chaptersInCache: chaptersCache.length
                            });
                        }

                        return updatedSummary;
                    });

                    // Also update cacheManager with the final result
                    const finalSummary = queryClient.getQueryData(['story-project-summary', projectState.projectId]);
                    if (finalSummary) {
                        cacheManager.set('project-summary', projectState.projectId, finalSummary, 5 * 60 * 1000);
                    }

                    // ✅ CRITICAL: Use longer staleTime + disable reconnect refetch
                    queryClient.setQueryDefaults(['story-project-summary', projectState.projectId], {
                        staleTime: 10 * 60 * 1000,
                        refetchOnMount: false,
                        refetchOnWindowFocus: false,
                        refetchOnReconnect: false,
                    });
                }

                // Delay invalidation to avoid race conditions with our cache updates
                setTimeout(() => {
                    // Only invalidate summaries list for dashboard updates
                    queryClient.invalidateQueries({queryKey: ['/api/story-projects/summaries']});
                }, 1000);

                console.log('✅ Updated React Query cache, cacheManager, and localStorage with batch preview content:', {
                    successCount,
                    totalChapters: updatedChapters.length,
                    chaptersWithPreviews: updatedChapters.filter(ch => ch.previewContent).length
                });

                toast({ title: "Success", description: `${successCount} chapter previews generated and saved.` });
            } else if (chaptersToProcess.length > 0) {
                toast({ title: "Info", description: `No chapter previews generated. Issues with word counts or eligibility.` });
            }
        } catch (e: any) { 
            lastError = e;
            setError(`Batch preview generation failed for "${chaptersToProcess[successCount]?.title || 'a chapter'}": ${e.message}`);
            toast({ title: "AI Error", description: e.message, variant: "destructive" });
            if (e.rawResponse) modalControls.openDebugResponseModal(e.rawResponse);
        } finally {
            setLoadingState('genAllShortPreviews', false);
            if (!lastError && successCount === 0 && chaptersToProcess.length > 0) setError("No chapters eligible or successfully processed for previews.");
        }
    }, [projectState, setLoadingState, setError, modalControls, clearError, setProjectState, toast, queryClient]);

    const handleReviseText = useCallback(async () => {
        // Note: API keys are handled server-side with environment variables
        if (!activeChapter || !coreState.activeVersion || !revisionInput.trim()) { setError("Active chapter, version, and revision instructions required."); return; }
        if (!projectState) { setError("No project loaded."); return; }
        const wordCount = parseInt(revisionWordCount, 10);
        if (isNaN(wordCount) || wordCount <= 0) { setError("Invalid expected word count for revision."); return; }

        setLoadingState('reviseText', true); setError(null);
        const chapterContext = {
            title: activeChapter.title, summary: activeChapter.summary, geography: activeChapter.geography,
            charactersInvolved: activeChapter.charactersInvolved, characterActions: activeChapter.characterActions, soulOfChapter: activeChapter.soulOfChapter,
        };
        const chapterVitalsData = activeChapter.vitals || initialChapterVitals;
        const currentContentForApi = coreState.activeVersion.content;

        aiMutation.mutate({
            task: 'chapter-revise',
            payload: {
                outlineIdea: projectState.outlineIdea,
                currentContent: currentContentForApi,
                currentSummary: coreState.activeVersion.generatedSummary,
                revisionInput,
                wordCount,
                worldBuildingData: projectState.blueprint.worldBuildingSuggestions,
                chapterVitalsData,
                chapterContext
            }
        });
    }, [apiKey, activeChapter, coreState.activeVersion, revisionInput, revisionWordCount, projectState, setLoadingState, setError, modalControls, aiMutation, setRevisionInput, editor]);

    return {
        handleGenerateChapterPreview,
        handleGenerateFullTextFromPreview,
        handleGenerateFullTextForAllPreviewedChapters,
        handleGenerateShortPreviewForAllChapters,
        handleReviseText,
    };
};
